// ============================================================================
// TaskCard — editable proposal card for propose_task. "Do it →" inserts
// into facility_tasks and updates the audit row in copilot_actions.
// ============================================================================

import { useMemo, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import type { CopilotProposalBase } from '../../../shared/types';
import { CardShell } from './card-shell';
import { FacilityCombobox } from './facility-combobox';
import { NO_BUILDING, NO_BUILDING_VALUE } from '../../tasks/no-building';

const PRIORITIES = ['High', 'Medium', 'Low'] as const;

interface FacilityOpt {
  facility_id: string;
  facility_name: string;
}

interface TaskCardProps {
  proposal: CopilotProposalBase;
  facilities: FacilityOpt[];
  isCommitting: boolean;
  onDoIt: (
    edited_input: Record<string, unknown>,
    was_edited: boolean,
  ) => void;
  onDiscard: () => void;
}

export function TaskCard({
  proposal,
  facilities,
  isCommitting,
  onDoIt,
  onDiscard,
}: TaskCardProps) {
  const initial = proposal.input as {
    facility_id?: string;
    description?: string;
    priority?: string;
    due_date?: string | null;
  };

  // No facility in the draft means Ask AI drafted it as not about one building.
  const [facility_id, setFacilityId] = useState(initial.facility_id ?? NO_BUILDING_VALUE);
  const choices = useMemo(
    () => [{ facility_id: NO_BUILDING_VALUE, facility_name: NO_BUILDING }, ...facilities],
    [facilities],
  );
  const [description, setDescription] = useState(initial.description ?? '');
  const [priority, setPriority] = useState(initial.priority ?? 'Medium');
  const [dueDate, setDueDate] = useState(initial.due_date ?? '');

  const wasEdited = useMemo(() => {
    return (
      facility_id !== (initial.facility_id ?? NO_BUILDING_VALUE) ||
      description !== (initial.description ?? '') ||
      priority !== (initial.priority ?? 'Medium') ||
      (dueDate || null) !== (initial.due_date ?? null)
    );
  }, [facility_id, description, priority, dueDate, initial]);

  const handleDoIt = () => {
    onDoIt(
      {
        facility_id,
        description,
        priority,
        due_date: dueDate || null,
      },
      wasEdited,
    );
  };

  const accent =
    priority === 'High' ? 'border-st-danger-border-strong' : 'border-st-info-border';

  return (
    <CardShell
      title="Task"
      icon={<ClipboardList className="h-4 w-4" />}
      accentClass={accent}
      status={proposal.status}
      committedLabel={
        proposal.target_row_id ? `Saved as ${proposal.target_row_id}` : 'Saved'
      }
      isCommitting={isCommitting}
      onDoIt={handleDoIt}
      onDiscard={onDiscard}
    >
      <Field label="Facility">
        <FacilityCombobox
          value={facility_id}
          options={choices}
          onChange={setFacilityId}
        />
      </Field>

      <Field label="Description">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className={`${inputClass} resize-none`}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Priority">
          <div className="flex gap-1">
            {PRIORITIES.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPriority(p)}
                className={`flex-1 rounded-md px-2 py-1 text-xs font-medium ${
                  priority === p
                    ? p === 'High'
                      ? 'bg-st-danger-solid text-st-solid-ink'
                      : p === 'Low'
                      ? 'bg-st-neutral text-st-solid-ink'
                      : 'bg-tcs-inverse-soft text-tcs-inverse-ink'
                    : 'bg-tcs-surface text-tcs-text-secondary hover:bg-tcs-surface-strong'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Due date">
          <input
            type="date"
            value={dueDate ?? ''}
            onChange={(e) => setDueDate(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>
    </CardShell>
  );
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
